import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { X, Trash2, Wallet, FileStack, Layers } from "lucide-react";

export interface BulkPatch {
  expenseAccount?: string;
  /** Number between 0 and 100 — applied as % IRPF to all selected invoices. Use 0 to clear. */
  irpfRate?: number;
  /** Serie del libro de IVA; cadena vacía = volver a la serie principal. */
  seriesPrefix?: string;
  seriesStart?: number;
}

interface BulkActionsBarProps {
  selectedCount: number;
  onApplyPatch: (patch: BulkPatch) => void;
  onDelete: () => void;
  onMerge: () => void;
  onClear: () => void;
  /** Series ya usadas en la sesión (para elegir rápido). */
  existingSeries?: { prefix: string; start: number; isMain: boolean }[];
}

const BulkActionsBar = ({
  selectedCount,
  onApplyPatch,
  onDelete,
  onMerge,
  onClear,
  existingSeries = [],
}: BulkActionsBarProps) => {
  const [seriesValue, setSeriesValue] = useState("");
  const [seriesStartValue, setSeriesStartValue] = useState("1");
  const [accountValue, setAccountValue] = useState("");
  
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmMerge, setConfirmMerge] = useState(false);
  const [confirmLargePatch, setConfirmLargePatch] = useState<null | (() => void)>(null);

  if (selectedCount === 0) return null;

  const guardLargeChange = (apply: () => void) => {
    if (selectedCount > 20) {
      setConfirmLargePatch(() => apply);
    } else {
      apply();
    }
  };

  return (
    <>
      <div className="fixed inset-x-0 bottom-0 z-40 flex justify-center print:hidden">
        <div className="flex flex-wrap items-center gap-2 w-full justify-center border-t bg-background px-4 py-3 shadow-[0_-4px_12px_-6px_hsl(var(--foreground)/0.15)]">
          <span className="text-sm font-medium">
            {selectedCount} {selectedCount === 1 ? "factura seleccionada" : "facturas seleccionadas"}
          </span>
          <span className="mx-1 h-5 w-px bg-border" />

          <Popover>
            <PopoverTrigger asChild>
              <Button size="sm" variant="outline">
                <Wallet className="h-4 w-4" /> Cuenta de gasto
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-64 space-y-2">
              <p className="text-xs text-muted-foreground">Asignar la misma cuenta a todas las seleccionadas:</p>
              <Input
                placeholder="Ej: 6290000"
                value={accountValue}
                onChange={(e) => setAccountValue(e.target.value)}
                inputMode="numeric"
              />
              <Button
                size="sm"
                className="w-full"
                disabled={!accountValue.trim()}
                onClick={() => {
                  const acc = accountValue.trim();
                  guardLargeChange(() => {
                    onApplyPatch({ expenseAccount: acc });
                    setAccountValue("");
                  });
                }}
              >
                Aplicar
              </Button>
            </PopoverContent>
          </Popover>

          <span className="mx-1 h-5 w-px bg-border" />

          <Popover>
            <PopoverTrigger asChild>
              <Button size="sm" variant="outline">
                <Layers className="h-4 w-4" /> Serie
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-72 space-y-3">
              <p className="text-xs text-muted-foreground">Pasar las seleccionadas a otra serie. Cada serie se numera por fecha de forma independiente.</p>
              {existingSeries.length > 1 && (
                <div className="flex flex-wrap gap-1.5">
                  {existingSeries.map((s) => (
                    <Button
                      key={s.prefix || "_main"}
                      size="sm"
                      variant="secondary"
                      onClick={() => guardLargeChange(() => onApplyPatch(s.isMain ? { seriesPrefix: "" } : { seriesPrefix: s.prefix, seriesStart: s.start }))}
                    >
                      {s.isMain ? `Principal${s.prefix ? ` (${s.prefix})` : ""}` : s.prefix}
                    </Button>
                  ))}
                </div>
              )}
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <Label htmlFor="bulk-series" className="text-xs">Nueva serie</Label>
                  <Input id="bulk-series" placeholder="Ej: B" value={seriesValue} onChange={(e) => setSeriesValue(e.target.value.toUpperCase())} />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="bulk-series-start" className="text-xs">Nº de IVA inicial</Label>
                  <Input id="bulk-series-start" inputMode="numeric" value={seriesStartValue} onChange={(e) => setSeriesStartValue(e.target.value.replace(/\D/g, ""))} />
                </div>
              </div>
              <Button
                size="sm"
                className="w-full"
                disabled={!seriesValue.trim()}
                onClick={() => {
                  const sp = seriesValue.trim();
                  const st = Math.max(1, parseInt(seriesStartValue) || 1);
                  guardLargeChange(() => {
                    onApplyPatch({ seriesPrefix: sp, seriesStart: st });
                    setSeriesValue("");
                    setSeriesStartValue("1");
                  });
                }}
              >
                Aplicar serie
              </Button>
              {existingSeries.length > 1 && (
                <Button size="sm" variant="ghost" className="w-full" onClick={() => guardLargeChange(() => onApplyPatch({ seriesPrefix: "" }))}>
                  Volver a la serie principal
                </Button>
              )}
            </PopoverContent>
          </Popover>

          <span className="mx-1 h-5 w-px bg-border" />

          <Button
            size="sm"
            variant="outline"
            disabled={selectedCount < 2}
            onClick={() => setConfirmMerge(true)}
          >
            <FileStack className="h-4 w-4" /> Unir PDF
          </Button>

          <Button size="sm" variant="outline" className="text-destructive hover:text-destructive" onClick={() => setConfirmDelete(true)}>
            <Trash2 className="h-4 w-4" /> Desechar
          </Button>


          <span className="mx-1 h-5 w-px bg-border" />

          <Button size="sm" variant="ghost" onClick={onClear}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Desechar {selectedCount} facturas?</AlertDialogTitle>
            <AlertDialogDescription>
              Se moverán a la papelera. Podrás recuperarlas mientras no finalices la sesión.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                onDelete();
                setConfirmDelete(false);
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Desechar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmMerge} onOpenChange={setConfirmMerge}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Unir {selectedCount} documentos en un único PDF?</AlertDialogTitle>
            <AlertDialogDescription>
              Los originales se moverán a la papelera y el archivo unido se procesará como una nueva factura.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                onMerge();
                setConfirmMerge(false);
              }}
            >
              Unir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>


      <AlertDialog open={confirmLargePatch !== null} onOpenChange={(open) => !open && setConfirmLargePatch(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Aplicar a {selectedCount} facturas?</AlertDialogTitle>
            <AlertDialogDescription>
              Vas a modificar muchas facturas a la vez. Confirma para continuar.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                confirmLargePatch?.();
                setConfirmLargePatch(null);
              }}
            >
              Aplicar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default BulkActionsBar;
